import { useMemo } from 'react';
import { Radio } from '@prezzee/components';
import Button from '@prezzee/ribbon-ui/lib/components/Button';
import Typography from '@prezzee/ribbon-ui/lib/components/Typography';
import Box from '@prezzee/ribbon-ui/lib/components/Box';
import { SubmitHandler, useForm } from 'react-hook-form';
import { css } from '@emotion/css';
import { spacing, colors } from '@prezzee/tokens';
import sortBy from 'lodash/sortBy';

import { PickSectionInputs, STEP } from 'models/purchaseForm.model';
import { ActionType } from 'reducers/ProductPageReducer';
import { useProduct } from 'contexts/ProductPageContext';
import useAuth from 'contexts/AuthContext';
import useSegment from 'hooks/useSegment';

const style = {
  valSelector: css`
    display: block;
    height: 50px;
    min-width: 70px;
  `,
  fieldset: css`
    & + fieldset {
      margin-top: ${spacing.spacing06};
    }
  `,
  column: css`
    margin-top: ${spacing.spacing025};
  `,
  disabled: css`
    color: ${colors.gray300} !important;
  `,
  recipient: css`
    margin-top: ${spacing.spacing025};
  `,
};

interface PickSectionProps {
  preselectedValue?: string;
}

const PickSection = ({ preselectedValue }: PickSectionProps): JSX.Element => {
  const {
    currency: { cc, symbol },
    sku,
    dispatch,
    state,
    purchaseInstanceId,
    configs,
    cardCustomisationState,
    isMultiPurchase,
  } = useProduct();
  const { user } = useAuth();
  const { checkoutStepCompleted, customElementInteraction, productAdded, productRemoved } = useSegment();

  const { handleSubmit, register, watch, formState } = useForm<PickSectionInputs>({
    mode: 'all',
    defaultValues: {
      purchaseType: state.formValues.purchaseType || (preselectedValue ? 'friend' : null),
      value: state.formValues.value || preselectedValue || null,
    },
  });

  const { value, purchaseType } = watch(['value', 'purchaseType']);

  const segmentStep = {
    step: 1,
    step_name: 'choose_step',
  };

  function segmentTrackPurchaseType(event: React.ChangeEvent<HTMLInputElement>) {
    const radioValue = (event.target as HTMLInputElement).value;

    customElementInteraction({
      element: radioValue === 'self' ? 'myself_cta' : 'someone_else_cta',
      purchase_instance_id: purchaseInstanceId,
      ...segmentStep,
    });
  }

  /**
   *  Send to preview and pay section if self checkout else continue through normal flow.
   */
  const onSubmit: SubmitHandler<PickSectionInputs> = values => {
    const isSelfPurchase = values.purchaseType === 'self';

    dispatch({ type: ActionType.SetFormValues, payload: values });
    dispatch({ type: ActionType.SetPreviousStep, payload: 'choose_step' });

    checkoutStepCompleted({
      element: 'save_and_continue_cta',
      purchase_instance_id: purchaseInstanceId,
      journey: purchaseType === 'friend' ? 'someone_else' : 'myself',
      price: parseFloat(value),
      ...segmentStep,
    });

    if (isSelfPurchase) {
      if (user) {
        dispatch({ type: ActionType.SetStep, payload: STEP.PREVIEW });
      } else {
        dispatch({ type: ActionType.SetStep, payload: STEP.SEND });
      }
    } else {
      dispatch({ type: ActionType.SetStep, payload: STEP.WRAP });
    }
  };

  const handleProductAddedRemoved = (denominationValue: string) => () => {
    const prevDenominationValue = state?.formValues?.previousValue;

    const customTheme = cardCustomisationState?.customThemeDesigns?.[0]?.image;
    const selectedCardDesign =
      cardCustomisationState?.selectedCardDesign?.isTemplate && customTheme
        ? customTheme
        : cardCustomisationState?.selectedCardDesign?.imageUrl;

    const baseSegmentObject = {
      product_id: sku?.uid,
      sku: sku?.slug,
      name: sku?.displayName,
      quantity: 1,
      purchase_instance_id: purchaseInstanceId,
      step: 1,
      step_name: 'pick_step',
      categories: sku?.categories.map(categories => categories.name),
      category: sku?.categories.length > 0 && sku?.categories[0].name,
      image_url: selectedCardDesign || sku?.themes?.[0]?.image,
    };

    prevDenominationValue &&
      productRemoved({
        ...baseSegmentObject,
        price: parseFloat(prevDenominationValue),
      });

    productAdded({
      ...baseSegmentObject,
      price: parseFloat(denominationValue),
    });

    // Setting the previous denomination value
    dispatch({
      type: ActionType.SetFormValues,
      payload: {
        previousValue: denominationValue,
      },
    });
  };

  const valuesDisabled = useMemo(() => !purchaseType, [purchaseType]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset className={style.fieldset}>
        <Typography variant="subtitle1" as="legend">
          Who are you gifting?
        </Typography>
        <Box display="flex" flexWrap="wrap" gap="10px">
          <Box flex="1" className={style.recipient}>
            <Radio
              id={'value-buyForMyself'}
              name="purchaseType"
              value={'self'}
              ref={register({ required: true })}
              fullWidth
              variant="contained"
              unselected={purchaseType && purchaseType !== 'self'}
              label={'Myself'}
              className={style.valSelector}
              onChange={segmentTrackPurchaseType}
            />
          </Box>
          <Box flex="1" className={style.recipient}>
            <Radio
              id={'value-buyAsGift'}
              name="purchaseType"
              value={'friend'}
              ref={register({ required: true })}
              fullWidth
              variant="contained"
              unselected={purchaseType && purchaseType !== 'friend'}
              label={`Someone else ${
                isMultiPurchase ? `(up to ${configs.classicMultiPurchaseMaxRecipient.toString()})` : ''
              }`}
              className={style.valSelector}
              onChange={segmentTrackPurchaseType}
            />
          </Box>
        </Box>
      </fieldset>
      <fieldset className={style.fieldset}>
        <Typography variant="subtitle1" as="legend" className={valuesDisabled && style.disabled} marginBottom={'1'}>
          What&apos;s your gift value? ({cc})
        </Typography>
        {isMultiPurchase && purchaseType === 'friend' && (
          <Typography
            as="p"
            variant="body2"
            fontWeight={'normal'}
            color={'gray.600'}
            className={valuesDisabled && style.disabled}
          >
            If you&apos;re gifting a group of people, they will each get the amount you choose.
          </Typography>
        )}
        {sku.denominations && (
          <Box
            display="grid"
            gap="10px"
            mt="10px"
            gridTemplateColumns={{ base: 'repeat(3,1fr)', md: 'repeat(4, 1fr)', xl: 'repeat(5, 1fr)' }}
          >
            {sortBy(sku.denominations.map(denomination => Number(denomination.value))).map(
              (denominationValueAsNumber, index) => {
                const denominationValue = denominationValueAsNumber.toFixed(2);
                return (
                  <Box key={index}>
                    <Radio
                      id={`value-${denominationValue}`}
                      name="value"
                      value={denominationValue}
                      ref={register({ required: true })}
                      fullWidth
                      variant="contained"
                      unselected={value && denominationValue !== value}
                      label={symbol + denominationValue.replace(/\.00/g, '')}
                      className={style.valSelector}
                      onClick={handleProductAddedRemoved(denominationValue)}
                      disabled={valuesDisabled}
                    />
                  </Box>
                );
              }
            )}
          </Box>
        )}
      </fieldset>
      <Button type="submit" fullWidth size="lg" mt="6" mb="4" isDisabled={!formState.isValid}>
        Save and continue
      </Button>
    </form>
  );
};

export default PickSection;
